// src/screens/VerificationScreen.js
// R6 (STEP3_5A_TIERS_DESIGN.md 3.13.3, J7v): an Email row. "Confirmed" is
// Firebase Auth's emailVerified on the account (reloaded on focus and on
// window focus), never users/{uid}.emailVerified (the browser can write it).
// "Send confirmation email" sends OUR link (sendEmailConfirmation, return
// route 'verification'); the address shows masked only. "Confirm" is for
// email, "verify" for ID and age.
import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  ActivityIndicator,
} from 'react-native';
import { useIsFocused } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import { getAuth } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import Footer from '../components/Footer';
import { useError } from '../components/ErrorContext';
import {
  sendEmailConfirmation,
  membershipErrorKind,
  membershipErrorMessage,
  logMembershipError,
} from '../utils/membershipPayment';
import {
  EMAIL_ROW,
  verificationSentLine,
  sendLimitLine,
  WRONG_ADDRESS_LINE,
  NO_ADDRESS_LINE,
} from '../utils/membershipJoin';
import { formatCooldown } from '../utils/spendingLimit';

// The account's Auth emailVerified after a reload: true / false, or null
// when signed out (the row is hidden) or unreadable (the last value stands).
async function readEmailConfirmed() {
  const user = getAuth().currentUser;
  if (!user) return null;
  try {
    await user.reload();
  } catch (_) {
    return null;
  }
  return getAuth().currentUser?.emailVerified === true;
}

export default function VerificationScreen({ navigation }) {
  const { showError } = useError();
  const [userData, setUserData] = useState(null);
  const [isLoading, setLoading] = useState(true);
  const isFocused = useIsFocused();
  // R6 Email row (J7v): Auth's emailVerified (null = signed out: no row),
  // the masked address a send went to, a refusal line, and sends refused
  // until (J9r).
  const [emailConfirmed, setEmailConfirmed] = useState(() => {
    const u = getAuth().currentUser;
    return u ? u.emailVerified === true : null;
  });
  const [emailSentTo, setEmailSentTo] = useState(null);
  const [emailNote, setEmailNote] = useState(null);
  // The server said the account has no email address (email-missing).
  const [emailMissing, setEmailMissing] = useState(false);
  const [emailBusy, setEmailBusy] = useState(false);
  const [sendBlockedUntil, setSendBlockedUntil] = useState(0);
  const [cooldownTick, setCooldownTick] = useState(0);

  const refreshEmail = useCallback(async () => {
    const confirmed = await readEmailConfirmed();
    if (confirmed !== null) setEmailConfirmed(confirmed);
  }, []);

  // Re-check when the tab regains focus (the link opens in another tab).
  useEffect(() => {
    if (!isFocused || typeof window === 'undefined' || typeof window.addEventListener !== 'function') return undefined;
    const onFocus = () => { refreshEmail(); };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [isFocused, refreshEmail]);

  // J9r counts down (a re-render each half minute, and right when it ends).
  useEffect(() => {
    const left = sendBlockedUntil - Date.now();
    if (!(left > 0)) return undefined;
    const t = setTimeout(() => setCooldownTick((n) => n + 1), Math.max(1000, Math.min(30000, left + 250)));
    return () => clearTimeout(t);
  }, [sendBlockedUntil, cooldownTick]);

  const sendConfirmation = async () => {
    if (emailBusy || sendBlockedUntil > Date.now()) return;
    setEmailBusy(true);
    setEmailNote(null);
    try {
      const res = await sendEmailConfirmation({ returnTo: 'verification' });
      if (res?.alreadyConfirmed === true) {
        setEmailConfirmed(true);
        await refreshEmail();
        return;
      }
      if (typeof res?.maskedEmail === 'string' && res.maskedEmail) setEmailSentTo(res.maskedEmail);
      const cd = Number(res?.cooldownMs) || 0;
      setSendBlockedUntil(cd > 0 ? Date.now() + cd : 0);
    } catch (err) {
      logMembershipError('sendEmailConfirmation', err);
      const retryAtMs = Number(err?.details?.retryAtMs);
      if (membershipErrorKind(err) === 'email_limit' && Number.isFinite(retryAtMs) && retryAtMs > Date.now()) {
        setSendBlockedUntil(retryAtMs);
      } else if (membershipErrorKind(err) === 'email_missing') {
        // No address at all: the no-address line shows once, with no Send
        // button (the join panel's R6C-4 rule; R6 copy read 13.2 V2).
        setEmailMissing(true);
      } else {
        setEmailNote(membershipErrorMessage(err));
      }
    } finally {
      setEmailBusy(false);
    }
  };

  // Re-fetch user data every time the screen comes into focus
  useEffect(() => {
    const loadUserData = async () => {
      try {
        const auth = getAuth();
        const user = auth.currentUser;
        if (user) {
          refreshEmail();
          const docRef = doc(db, 'users', user.uid);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            setUserData(docSnap.data());
          }
        }
      } catch (error) {
        console.error('Failed to load user data:', error);
      } finally {
        setLoading(false);
      }
    };

    if (isFocused) {
      setLoading(true);
      loadUserData();
    }
  }, [isFocused, refreshEmail]);

  // Helper function to determine button style based on verification status
  const getStatusStyle = (status) => {
    if (status === true || status === 'verified') return styles.verifiedButton;
    if (status === 'pending') return styles.pendingButton;
    return styles.button;
  };

  // Helper function to set the button text based on verification status
  const getButtonText = (label, status) => {
    if (label === 'Terms of Service' && status === 'verified') {
      return `✓ ${label} Accepted`;
    }
    if (status === true || status === 'verified') return `✓ ${label} Verified`;
    if (status === 'pending') return `⏳ ${label} Pending`;
    return label;
  };

  // Check if all verifications are complete
  const isFullyVerified = () => {
    const tosStatus = userData?.verification?.tos?.status || 'unverified';
    const phoneStatus = userData?.phoneVerified || false;
    const idStatus = userData?.verification?.governmentId?.status || 'unverified';
    
    return tosStatus === 'verified' && phoneStatus === true && idStatus === 'verified';
  };

  if (isLoading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#FCD000" />
      </View>
    );
  }

  // The Email row (J7v): "Confirmed" / "Not confirmed"; while not
  // confirmed, "Send confirmation email", then the masked address it went
  // to (the R6 copy read's line for this row) with J9w under it, and J9r
  // while sends are refused. An account with no email address gets the
  // no-address line and no Send button (R6 copy read 13.2 V2, 10-18).
  const renderEmailRow = () => {
    const left = sendBlockedUntil - Date.now();
    const blocked = left > 0;
    const noAddress = emailMissing || !getAuth().currentUser?.email;
    if (noAddress && !emailConfirmed) {
      return (
        <View style={styles.emailRow}>
          <View style={styles.buttonContent}>
            <Ionicons name="mail-outline" size={20} color="#fff" style={styles.emailIcon} />
            <Text style={styles.buttonText}>{EMAIL_ROW.title}</Text>
            <Text style={[styles.emailStatus, styles.emailStatusNo]}>{EMAIL_ROW.notConfirmed}</Text>
          </View>
          {/* No J7v body here: it speaks of "this address", and there is none. */}
          <Text style={styles.emailFoot}>{NO_ADDRESS_LINE}</Text>
        </View>
      );
    }
    return (
      <View style={[styles.emailRow, emailConfirmed && styles.emailRowConfirmed]}>
        <View style={styles.buttonContent}>
          <Ionicons name="mail-outline" size={20} color="#fff" style={styles.emailIcon} />
          <Text style={styles.buttonText}>{EMAIL_ROW.title}</Text>
          <Text style={[styles.emailStatus, emailConfirmed ? styles.emailStatusOk : styles.emailStatusNo]}>
            {emailConfirmed ? EMAIL_ROW.confirmed : EMAIL_ROW.notConfirmed}
          </Text>
        </View>
        <Text style={[styles.emailBody, emailConfirmed && styles.emailBodyConfirmed]}>{EMAIL_ROW.body}</Text>
        {emailConfirmed ? null : (
          <>
            {emailSentTo ? <Text style={styles.emailSent}>{verificationSentLine(emailSentTo)}</Text> : null}
            {emailNote ? <Text style={styles.emailNote}>{emailNote}</Text> : null}
            {blocked ? <Text style={styles.emailNote}>{sendLimitLine(formatCooldown(left))}</Text> : null}
            <TouchableOpacity
              style={[styles.emailButton, (emailBusy || blocked) && styles.emailButtonDisabled]}
              onPress={sendConfirmation}
              disabled={emailBusy || blocked}
              accessibilityRole="button"
            >
              {emailBusy ? <ActivityIndicator size="small" color="#000" /> : <Text style={styles.emailButtonText}>{EMAIL_ROW.button}</Text>}
            </TouchableOpacity>
            {emailSentTo ? <Text style={styles.emailFoot}>{WRONG_ADDRESS_LINE}</Text> : null}
          </>
        )}
      </View>
    );
  };

  // Determine the status for each verification type
  const tosStatus = userData?.verification?.tos?.status || 'unverified';
  const phoneStatus = userData?.phoneVerified || false;
  const idStatus = userData?.verification?.governmentId?.status || 'unverified';

  return (
    <View style={styles.outerContainer}>
      <ScrollView contentContainerStyle={styles.scrollContainer}>
        <Text style={styles.title}>Verification Center</Text>
        <Text style={styles.subtitle}>
          Complete the following steps to become a fully verified CritCash user.
        </Text>

        {/* Fully Verified Badge */}
        {isFullyVerified() && (
          <View style={styles.fullyVerifiedBadge}>
            <Text style={styles.fullyVerifiedIcon}>🎉</Text>
            <Text style={styles.fullyVerifiedText}>You're fully verified!</Text>
          </View>
        )}

        {/* --- Terms of Service Button --- */}
        <TouchableOpacity
          style={getStatusStyle(tosStatus)}
          onPress={() =>
            tosStatus === 'unverified' && navigation.navigate('TosAgreement')
          }
          disabled={tosStatus !== 'unverified'}
        >
          <View style={styles.buttonContent}>
            <Text style={styles.buttonIcon}>📜</Text>
            <Text style={styles.buttonText}>
              {getButtonText('Terms of Service', tosStatus)}
            </Text>
          </View>
        </TouchableOpacity>

        {/* --- Email (R6, J7v) --- */}
        {emailConfirmed !== null ? renderEmailRow() : null}

        {/* --- Phone Verification Button --- */}
        <TouchableOpacity
          style={getStatusStyle(phoneStatus)}
          onPress={() =>
            !phoneStatus &&
            navigation.navigate('PhoneVerificationScreen', { userData })
          }
          disabled={phoneStatus}
        >
          <View style={styles.buttonContent}>
            <Text style={styles.buttonIcon}>📱</Text>
            <Text style={styles.buttonText}>
              {getButtonText('Phone Number', phoneStatus)}
            </Text>
          </View>
        </TouchableOpacity>

        {/* --- ID & Age Verification Button --- */}
        <TouchableOpacity
          style={getStatusStyle(idStatus)}
          onPress={() => navigation.navigate('IdVerification')}
          disabled={idStatus === 'verified'}
        >
          <View style={styles.buttonContent}>
            <Text style={styles.buttonIcon}>🪪</Text>
            <Text style={styles.buttonText}>
              {getButtonText('Identity (ID & Age)', idStatus)}
            </Text>
            {idStatus === 'verified' && userData?.verification?.governmentId?.ageVerified && (
              <View style={styles.ageBadge}>
                <Text style={styles.ageBadgeText}>18+</Text>
              </View>
            )}
          </View>
        </TouchableOpacity>

        {/* Info Box */}
        <View style={styles.infoBox}>
          <Text style={styles.infoTitle}>Why verify?</Text>
          <Text style={styles.infoText}>
            Verification helps us keep CritCash safe and compliant. Identity and age (18+)
            verification is the only requirement to receive compensation. Nothing is credited
            before you verify.
          </Text>
        </View>

        {/* Bank Account Notice */}
        <View style={styles.noticeBox}>
          <Text style={styles.noticeIcon}>🏦</Text>
          <Text style={styles.noticeText}>
            Payable earnings are paid automatically to the payout account you connect.
            You can set one up from your Wallet.
          </Text>
        </View>

        <TouchableOpacity
          onPress={() => (navigation.canGoBack() ? navigation.goBack() : navigation.navigate('Lobby'))}
          style={styles.backButton}
        >
          <Text style={styles.backButtonText}>← Go Back</Text>
        </TouchableOpacity>
      </ScrollView>
      <Footer navigation={navigation} />
    </View>
  );
}

const styles = StyleSheet.create({
  outerContainer: {
    flex: 1,
    backgroundColor: '#000',
  },
  scrollContainer: {
    flexGrow: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  loadingContainer: {
    flex: 1,
    backgroundColor: '#000',
    justifyContent: 'center',
    alignItems: 'center',
  },
  title: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#FCD000',
    marginBottom: 10,
    textAlign: 'center',
  },
  subtitle: {
    fontSize: 16,
    color: '#CCC',
    textAlign: 'center',
    marginBottom: 30,
    paddingHorizontal: 10,
  },
  
  // Fully Verified Badge
  fullyVerifiedBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(46, 204, 113, 0.15)',
    borderWidth: 1,
    borderColor: '#2ECC71',
    borderRadius: 12,
    padding: 16,
    marginBottom: 24,
    width: '100%',
  },
  fullyVerifiedIcon: {
    fontSize: 24,
    marginRight: 12,
  },
  fullyVerifiedText: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#2ECC71',
  },
  
  // Buttons
  button: {
    width: '100%',
    backgroundColor: '#222',
    padding: 16,
    borderRadius: 12,
    marginBottom: 12,
  },
  verifiedButton: {
    width: '100%',
    backgroundColor: '#2ECC71',
    padding: 16,
    borderRadius: 12,
    marginBottom: 12,
  },
  pendingButton: {
    width: '100%',
    backgroundColor: '#F39C12',
    padding: 16,
    borderRadius: 12,
    marginBottom: 12,
  },
  buttonContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  buttonIcon: {
    fontSize: 20,
    marginRight: 12,
  },
  buttonText: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#fff',
    flex: 1,
  },
  ageBadge: {
    backgroundColor: 'rgba(0,0,0,0.3)',
    borderRadius: 6,
    paddingHorizontal: 8,
    paddingVertical: 2,
  },
  ageBadgeText: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#fff',
  },

  // Email row (R6, J7v)
  emailRow: {
    width: '100%',
    backgroundColor: '#222',
    padding: 16,
    borderRadius: 12,
    marginBottom: 12,
  },
  emailRowConfirmed: {
    backgroundColor: '#2ECC71',
  },
  emailIcon: {
    marginRight: 12,
  },
  emailStatus: {
    fontSize: 13,
    fontWeight: 'bold',
    marginLeft: 8,
  },
  emailStatusOk: {
    color: '#fff',
  },
  emailStatusNo: {
    color: '#F39C12',
  },
  emailBody: {
    fontSize: 13,
    color: '#bbb',
    lineHeight: 19,
    marginTop: 8,
  },
  emailBodyConfirmed: {
    color: '#fff',
  },
  emailSent: {
    fontSize: 13,
    color: '#EDEDED',
    lineHeight: 19,
    marginTop: 10,
  },
  emailNote: {
    fontSize: 13,
    color: '#f5c542',
    lineHeight: 19,
    marginTop: 10,
  },
  emailButton: {
    backgroundColor: '#FCD000',
    borderRadius: 10,
    paddingVertical: 10,
    alignItems: 'center',
    marginTop: 12,
  },
  emailButtonDisabled: {
    opacity: 0.5,
  },
  emailButtonText: {
    color: '#000',
    fontSize: 14,
    fontWeight: 'bold',
  },
  emailFoot: {
    fontSize: 12,
    color: '#999',
    lineHeight: 17,
    marginTop: 8,
  },
  
  // Info Box
  infoBox: {
    backgroundColor: '#111',
    borderRadius: 12,
    padding: 16,
    marginTop: 16,
    marginBottom: 12,
    width: '100%',
    borderWidth: 1,
    borderColor: '#222',
  },
  infoTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#FCD000',
    marginBottom: 8,
  },
  infoText: {
    fontSize: 14,
    color: '#888',
    lineHeight: 20,
  },
  
  // Notice Box
  noticeBox: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: '#1a1a2e',
    borderRadius: 12,
    padding: 16,
    marginBottom: 20,
    width: '100%',
    borderWidth: 1,
    borderColor: '#2a2a4e',
  },
  noticeIcon: {
    fontSize: 20,
    marginRight: 12,
    marginTop: 2,
  },
  noticeText: {
    flex: 1,
    fontSize: 13,
    color: '#888',
    lineHeight: 20,
  },
  
  // Back Button
  backButton: {
    marginTop: 10,
    paddingBottom: 20,
  },
  backButtonText: {
    color: '#fff',
    fontSize: 16,
    textDecorationLine: 'underline',
  },
});